import { useCallback } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { routeToPageSlug } from "@/features/auth/lib/page-routes";
import { exploreDataScope } from "@/features/dashboard/lib/explore-api-prefix";
import { resolveExploreModeAccess, tenantGrantsExploreMyBrand } from "@/features/dashboard/lib/explore-mode-access";
import { EXPLORE_ROUTES, parseExploreSearch, searchFromLocationHref } from "@/features/dashboard/lib/explore-search";
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store";

/**
 * Wraps `setActiveTenant` with client-side validation.
 *
 * Always re-runs the current route's `beforeLoad` guard after a switch so
 * navigation and page access stay in sync: a missing PAGE shows PageError
 * in place (AD-1622, no redirect), and switching back to a tenant that
 * grants the page recovers without a full refresh.
 */
export function useTenantSwitch() {
  const tenants = useAuth((s) => s.tenants);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const setActiveTenant = useAuth((s) => s.setActiveTenant);
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const switchTenant = useCallback(
    (tenantId: string) => {
      if (tenantId === activeTenantId) return;
      const target = tenants.find((t) => t.id === tenantId);
      if (!target) return;

      const currentSlug = routeToPageSlug(pathname);

      const onExplore = EXPLORE_ROUTES.has(pathname);
      const modeAccess = resolveExploreModeAccess(
        target.productLine,
        onExplore
          ? tenantGrantsExploreMyBrand(exploreDataScope(pathname), target.permissions)
          : true,
      );

      // Reset before auth so the first paint cannot send the previous tenant's
      // products with the new X-Tenant-Id. Header effect then no-ops.
      useDashboardFiltersStore.getState().resetForTenant(tenantId, {
        hydratingFromUrl: false,
      });
      if (modeAccess.lockedMode) {
        useDashboardFiltersStore.getState().setMode(modeAccess.lockedMode);
      }
      setActiveTenant(tenantId);

      // Unknown route — no permission guard, stay put
      if (currentSlug == null) return;

      const hasCurrentPage = target.permissions.some(
        (p) => p.type === "PAGE" && p.route === currentSlug,
      );

      if (hasCurrentPage && onExplore) {
        // Product-line lock wins over the previous URL. CORE_AND_COMP keeps My
        // Brand only when the target role still grants that page's group.
        const current = parseExploreSearch(
          searchFromLocationHref(router.state.location.href),
        );
        const search =
          modeAccess.lockedMode === "my-brand"
            ? { mode: "my-brand" as const }
            : modeAccess.lockedMode === "competitors"
              ? {}
              : current.mode === "my-brand" &&
                  tenantGrantsExploreMyBrand(exploreDataScope(pathname), target.permissions)
                ? { mode: current.mode }
                : {};
        router.navigate({ to: pathname, search: search as never, replace: true });
      }

      // Re-run beforeLoad in both directions: deny → PageError, grant → recover.
      void router.invalidate();
    },
    [tenants, activeTenantId, setActiveTenant, pathname, router],
  );

  return switchTenant;
}
